import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  FolderTreeResponse,
  IngestStatus,
  SharepointHealthResponse,
  StatusSummaryResponse,
  SyncRunListResponse,
  SyncRunModel,
} from '@/models/sharepoint';
import { filesQueryKey, LibraryService } from '@/services/library.service';

const TERMINAL_STATUSES = new Set(['ok', 'completed', 'error', 'failed']);

const DEFAULT_DEPTH = 2;

const useSharepoint = (orgID?: string, orgIndex?: number) => {
  const queryClient = useQueryClient();
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const reloadDetectionDone = useRef(false);

  const {
    data: foldersData,
    isFetching: isFetchingFolders,
    isFetched: hasFolders,
  } = useQuery<FolderTreeResponse>({
    queryKey: ['sharepointFolders', orgID],
    queryFn: async () => {
      return await LibraryService.getSharepointFolders(DEFAULT_DEPTH, orgID);
    },
    staleTime: 1000 * 60 * 5,
  });

  const { data: healthData, isFetching: isFetchingHealth } =
    useQuery<SharepointHealthResponse>({
      queryKey: ['sharepoint-health', orgID],
      queryFn: () => LibraryService.getSharepointHealth(orgID),
      staleTime: 1000 * 60 * 5,
    });

  const isConnected = !!healthData;

  const {
    data: summaryData,
    isFetching: isFetchingSummary,
    isFetched: hasSummary,
  } = useQuery<StatusSummaryResponse>({
    queryKey: ['sharepoint-summary', orgID],
    queryFn: async () => {
      let res: StatusSummaryResponse = {
        status: 'error',
        total_files: 0,
        by_ingest_status: {
          [IngestStatus.FAILED]: 0,
          [IngestStatus.IN_PROGRESS]: 0,
          [IngestStatus.COMPLETED]: 0,
          [IngestStatus.PENDING]: 0,
        },
        by_upload_status: {},
        files_last_24h: 0,
      };

      try {
        res = await LibraryService.getSharepointSummary(undefined, orgID);
      } catch (error) {
        console.error('Error fetching SharePoint summary:', error);
      }

      return res;
    },
    enabled: isConnected,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: syncData,
    isFetching: isFetchingSyncs,
    isFetched: hasSyncs,
  } = useQuery<SyncRunListResponse>({
    queryKey: ['sharepoint-sync-runs', orgID],
    queryFn: async () => {
      let res: SyncRunListResponse;

      try {
        const response = await LibraryService.getSharepointSyncRuns();
        res = response;
      } catch (error) {
        console.error('Error fetching SharePoint sync runs:', error);
        res = {
          status: 'error',
          total_count: 0,
          paginated_count: 0,
          skip: 0,
          limit: 0,
          sync_runs: [],
        };
      }
      return res;
    },
    enabled: isConnected,
    staleTime: 1000 * 60 * 5,
  });

  // Reload detection: if the initial sync-runs list has a non-terminal run, start polling it
  useEffect(() => {
    if (reloadDetectionDone.current || !syncData?.sync_runs) return;
    reloadDetectionDone.current = true;
    const inProgressRun = syncData.sync_runs.find(
      (run) => !TERMINAL_STATUSES.has(run.status)
    );
    if (inProgressRun) {
      setActiveRunId(inProgressRun.mongodb_document_id);
    }
  }, [syncData]);

  const syncMutation = useMutation({
    mutationFn: () => LibraryService.triggerSharepointSync(orgID),
    onSuccess: (data) => {
      setActiveRunId(data.run_id);
      toast.success(
        'SharePoint sync triggered successfully. It may take a few minutes to see the results. You can keep this window open to get real-time updates on the sync status.',
        {
          autoClose: 10000,
        }
      );
    },
    onError: (error) => {
      toast.error(
        (error as Error).message || 'Failed to trigger SharePoint sync.'
      );
    },
  });

  const { data: activeRunData } = useQuery({
    queryKey: ['sharepoint-sync-run-status', activeRunId, orgID],
    queryFn: async () => {
      const data = await LibraryService.getSharepointSyncRunStatus(
        activeRunId!,
        orgID
      );

      return data;
    },
    enabled: !!activeRunId,
    refetchInterval: 3000,
    staleTime: 0,
  });

  useEffect(() => {
    if (!activeRunData) return;
    if (TERMINAL_STATUSES.has(activeRunData.status)) {
      setActiveRunId(null);
      if (activeRunData.status === 'ok') {
        toast.success('SharePoint sync completed successfully!');
        if (orgIndex !== undefined) {
          queryClient.resetQueries({ queryKey: filesQueryKey(orgIndex) });
        }
      } else {
        toast.error(
          `SharePoint sync ended with status: ${activeRunData.status}${
            activeRunData.error_message
              ? `. Error: ${activeRunData.error_message}`
              : ''
          }`
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['sharepoint-sync-runs', orgID],
      });
    }
  }, [activeRunData, orgID, orgIndex, queryClient]);

  const mergedSyncRuns: SyncRunModel[] = activeRunData
    ? [
        activeRunData,
        ...(syncData?.sync_runs ?? []).filter(
          (r) => r.mongodb_document_id !== activeRunData.mongodb_document_id
        ),
      ]
    : (syncData?.sync_runs ?? []);

  const isSyncInProgress = !!activeRunId || syncMutation.isPending;

  return {
    isConnected,
    folders: foldersData,
    summary: summaryData,
    syncRuns: mergedSyncRuns,
    health: healthData,
    isFetchingFolders,
    isFetchingSummary,
    isFetchingSyncs,
    isFetchingHealth,
    hasFolders,
    hasSummary,
    hasSyncs,
    triggerSync: syncMutation.mutate,
    isSyncInProgress,
  };
};

export default useSharepoint;
